import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, TextField, CircularProgress,
    IconButton, Chip, Stack, InputAdornment, Avatar, Tooltip
} from "@mui/material";
import {
    Visibility, Edit, Search, Delete
} from "@mui/icons-material";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function Allstudents() {
    const navigate = useNavigate();
    const [admissions, setAdmissions] = useState([]);
    const [filteredAdmissions, setFilteredAdmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchData();
    }, []);

    // Helper function to safely format Firestore Timestamps, ISO strings, or Date objects
    const formatTimestamp = (ts) => {
        if (!ts) return "-";
        try {
            let dateObj;
            if (typeof ts.toDate === "function") {
                dateObj = ts.toDate();
            } else if (ts.seconds) {
                dateObj = new Date(ts.seconds * 1000);
            } else {
                dateObj = new Date(ts);
            }

            if (isNaN(dateObj.getTime())) return "-";

            return dateObj.toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true
            });
        } catch (e) {
            return "-";
        }
    };

    // Helper function to extract numerical milliseconds for descending sorting
    const getMillis = (ts) => {
        if (!ts) return 0;
        if (typeof ts.toMillis === "function") return ts.toMillis();
        if (ts.seconds) return ts.seconds * 1000;
        const parsed = new Date(ts).getTime();
        return isNaN(parsed) ? 0 : parsed;
    };

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch records concurrently from both 'admission' and 'students' collections
            const [admSnap, studentsSnap] = await Promise.all([
                getDocs(collection(db, "admission")),
                getDocs(collection(db, "students"))
            ]);

            // Map student full names keyed by document ID
            const studentsMap = {};
            studentsSnap.docs.forEach((studentDoc) => {
                const sData = studentDoc.data();
                // Construct name from firstname, fathername, surname if available
                const nameParts = [sData.firstname, sData.fathername, sData.surname].filter(Boolean);
                const fullName = nameParts.length > 0 
                    ? nameParts.join(" ").trim() 
                    : (sData.name || sData.studentName || sData.fullName || "");
                
                studentsMap[studentDoc.id] = fullName;
            });

            const records = admSnap.docs.map((d) => {
                const data = d.data();
                const docId = d.id;

                // Match student doc ID with admission docId (or admission.studentId as fallback)
                const studentName = studentsMap[docId] || studentsMap[data.studentId] || "";

                return {
                    docId: docId,
                    id: data.id || docId,
                    studentId: data.studentId || docId,
                    studentName: studentName,
                    admissionType: data.admissionType || "-",
                    admno: data.admno || "-",
                    class: data.class || "-",
                    fathername: data.fathername || "-",
                    mothername: data.mothername || "-",
                    father_mobile1: data.father_mobile1 || "-",
                    father_mobile2: data.father_mobile2 || "-",
                    mother_mobile1: data.mother_mobile1 || "-",
                    photoURL: data.photoURL || "",
                    status: data.status || "Active",
                    studenttype: data.studenttype || "Regular",
                    schoolId: data.schoolId || "-",
                    grno: data.grno || "-",
                    penno: data.penno || "-",
                    saralno: data.saralno || "-",
                    transport: data.transport || "-",
                    createdAt: data.createdAt || null,
                    updatedAt: data.updatedAt || null
                };
            });

            // Sort descending by updatedAt (fallback to createdAt if updatedAt is missing)
            records.sort((a, b) => {
                const timeA = getMillis(a.updatedAt || a.createdAt);
                const timeB = getMillis(b.updatedAt || b.createdAt);
                return timeB - timeA;
            });

            setAdmissions(records);
            setFilteredAdmissions(records);
        } catch (e) {
            console.error("Error fetching admission data:", e);
            Swal.fire("Error", "Failed to load admission collection data", "error");
        } finally {
            setLoading(false);
        }
    };

    // Permanently Delete Admission Document from Firestore
    const handleDeleteAdmission = async (admissionDoc) => {
        try {
            const result = await Swal.fire({
                title: 'Delete Admission Permanently?',
                text: `Are you sure you want to permanently delete record ${admissionDoc.docId}? This action cannot be undone.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, Delete Permanently!',
                showLoaderOnConfirm: true,
                preConfirm: async () => {
                    try {
                        const admRef = doc(db, "admission", admissionDoc.docId);
                        await deleteDoc(admRef);
                        return true;
                    } catch (error) {
                        Swal.showValidationMessage(`Delete failed: ${error.message}`);
                    }
                },
                allowOutsideClick: () => !Swal.isLoading()
            });

            if (result.isConfirmed) {
                Swal.fire('Deleted!', 'Admission record has been permanently deleted.', 'success');
                
                const updated = admissions.filter(a => a.docId !== admissionDoc.docId);
                setAdmissions(updated);
                setFilteredAdmissions(prev => prev.filter(a => a.docId !== admissionDoc.docId));
            }
        } catch (error) {
            console.error("Error deleting admission:", error);
            Swal.fire("Error", "Failed to delete admission record", "error");
        }
    };

    const handleSearch = (txt) => {
        setSearch(txt);
        const term = txt.toLowerCase().trim();

        const filtered = admissions.filter((a) => {
            return (
                a.docId.toLowerCase().includes(term) ||
                a.studentName.toLowerCase().includes(term) ||
                a.fathername.toLowerCase().includes(term) ||
                a.mothername.toLowerCase().includes(term) ||
                a.father_mobile1.includes(term) ||
                a.mother_mobile1.includes(term) ||
                a.studenttype.toLowerCase().includes(term) ||
                a.status.toLowerCase().includes(term)
            );
        });

        setFilteredAdmissions(filtered);
        setPage(0);
    };

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" mb={2} spacing={2} alignItems="center">
                <Box>
                    <Typography variant="h6" fontWeight="900" color="primary">Admission Directory</Typography>
                    <Chip label={`Total Records: ${filteredAdmissions.length}`} size="small" color="secondary" sx={{ fontWeight: 'bold' }} />
                </Box>
                <TextField
                    placeholder="Search Student Name, Father, Mother, Mobile, Doc ID..."
                    size="small"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ width: 360 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        )
                    }}
                />
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                {loading ? (
                    <Box p={10} textAlign="center"><CircularProgress /></Box>
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Sr.</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Photo</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Doc / Student ID</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Student Name</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Parents Name</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Mobile Numbers</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Updated At</TableCell>
                                <TableCell align="right" sx={{ bgcolor: "#f8f9fa", fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAdmissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((a, index) => (
                                <TableRow key={a.docId} hover>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell>
                                        <Avatar src={a.photoURL} sx={{ width: 40, height: 40, border: '1px solid #ddd' }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                        <Typography variant="caption" display="block" fontWeight="bold" color="primary">{a.docId}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600" color="text.primary">
                                            {a.studentName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600">Father: {a.fathername}</Typography>
                                        <Typography variant="caption" color="text.secondary">Mother: {a.mothername}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">Father: {a.father_mobile1}</Typography>
                                        <Typography variant="caption" color="text.secondary">Mother: {a.mother_mobile1}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={a.studenttype} size="small" color={a.studenttype === 'Boarding' ? 'secondary' : 'default'} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={a.status} 
                                            size="small" 
                                            color={a.status === 'Active' ? 'success' : a.status === 'Cancelled' ? 'error' : 'default'} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" fontWeight="600" display="block" color="text.primary">
                                            {formatTimestamp(a.updatedAt || a.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <Tooltip title="View Student Profile">
                                                <IconButton size="small" color="info" onClick={() => navigate(`/student-profile/${a.studentId}`)}>
                                                    <Visibility fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Admission">
                                                <IconButton size="small" color="warning" onClick={() => navigate(`/admission/${a.docId}`)}>
                                                    <Edit fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Permanently">
                                                <IconButton size="small" color="error" onClick={() => handleDeleteAdmission(a)}>
                                                    <Delete fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredAdmissions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                                        No admission records found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={filteredAdmissions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </TableContainer>
        </Box>
    );
}

export default Allstudents;